/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import axios from "axios";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  broilersByFarmUrl,
  farmsForUserUrl,
  getBroilerSummaryUrl,
  getCatchSummaryUrl,
  getCostSummaryUrl,
  getCyclesUrl,
} from "api/endpoints";

const FACILITY_SELECTION_KEY = "facilityLandingSelections";
const SKIP_AUTH_CONFIG = { omnioOffline: { skipAuthHeader: true } };
const highlightCards = [
  {
    id: "active-facilities",
    title: "Facilities (Coming Soon)",
    value: "12",
    helper: "Synced in the last 24h",
    trend: "+2 vs last week",
  },
  {
    id: "avg-fcr",
    title: "Avg FCR (Coming Soon)",
    value: "1.62",
    helper: "Flock 42 · 34 days",
    trend: "-0.03 vs target",
  },
  {
    id: "growth-rate",
    title: "Growth Rate (Coming Soon)",
    value: "64 g/bird",
    helper: "Rolling 7 day avg",
    trend: "+4 g vs last week",
  },
  {
    id: "mortality",
    title: "Mortality (Coming Soon)",
    value: "1.4%",
    helper: "Current cycle",
    trend: "-0.2 pts week over week",
  },
];

const analyticsFilters = [
  { id: "farm", label: "Farm", options: [] },
  { id: "cycle", label: "Cycle", options: ["All Cycles"] },
  { id: "day", label: "Day", options: Array.from({ length: 36 }, (_, index) => index) },
  { id: "type", label: "Type", options: ["Summary", "Catch", "Costs"] },
];

const formatSummaryDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const date = new Date(`${datePart}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const formatCostSummaryNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCatchDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const sqlDateTime = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/);
  if (sqlDateTime) {
    const [, year, month, day, hour = "0", minute = "0", second = "0", milliseconds = "0"] =
      sqlDateTime;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      Number(milliseconds.padEnd(3, "0"))
    );
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCatchDateTime = (value) => {
  if (!value) return "-";
  const date = parseCatchDateTime(value);
  if (!date) return String(value);
  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

const formatPlacementAverage = (row) => {
  const value = row?.placementAvg ?? row?.docWeight;
  if (value == null) return "-";
  const average = Number(value);
  return Number.isFinite(average) ? average.toFixed(3) : value;
};

const getCatchWeightDifference = (row) =>
  row?.catchDelta ?? row?.weightDifference ?? row?.weightDiff ?? "-";

const exportExcelCsv = (filename, headers, rows) => {
  const escapeCell = (value) => {
    const text = value == null || value === "-" ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };
  const csv = [headers, ...rows].map((row) => row.map(escapeCell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const buildReportsUrl = (row) => {
  const params = new URLSearchParams();
  if (row?.farmId != null) params.set("farm", row.farmId);
  if (row?.facilityId != null) params.set("facility", row.facilityId);
  if (row?.cycleId != null) params.set("cycle", row.cycleId);
  return `/reports/facilities?${params.toString()}`;
};

function Analytics() {
  const { pathname } = useLocation();
  const isStandaloneAnalytics = pathname === "/analytics";
  const summaryTableRef = useRef(null);
  const floatingScrollbarRef = useRef(null);
  const [farmOptions, setFarmOptions] = useState([]);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [catchRows, setCatchRows] = useState([]);
  const [catchLoading, setCatchLoading] = useState(false);
  const [catchError, setCatchError] = useState("");
  const [costRows, setCostRows] = useState([]);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState("");
  const [catchNameOrder, setCatchNameOrder] = useState("asc");
  const [summarySort, setSummarySort] = useState({ orderBy: "cycleDay", order: "asc" });
  const [floatingScrollbar, setFloatingScrollbar] = useState({
    visible: false,
    left: 0,
    width: 0,
    contentWidth: 0,
  });
  const [filters, setFilters] = useState({
    farm: "",
    cycle: "",
    day: 0,
    type: "Summary",
  });

  const handleFilterChange = (filterId, value) => {
    setFilters((currentFilters) => ({ ...currentFilters, [filterId]: value }));
  };

  useEffect(() => {
    if (isStandaloneAnalytics) return undefined;
    if (typeof window === "undefined") return undefined;

    const sessionRaw = window.localStorage.getItem("OmnioLogin");
    const session = sessionRaw ? JSON.parse(sessionRaw) : null;
    const userId = session?.ID;
    if (!userId) return undefined;

    let storedFarmId = null;
    try {
      const storedRaw = window.localStorage.getItem(FACILITY_SELECTION_KEY);
      const storedSelection = storedRaw ? JSON.parse(storedRaw) : null;
      storedFarmId = storedSelection?.farmId != null ? `${storedSelection.farmId}` : null;
    } catch {
      storedFarmId = null;
    }

    let didCancel = false;
    const loadFarms = async () => {
      try {
        const response = await axios.get(farmsForUserUrl(userId));
        const farmsData = Array.isArray(response.data) ? response.data : [];
        const pick = (...values) =>
          values.find((value) => value !== undefined && value !== null && `${value}`.length > 0);
        const normalizedFarms = farmsData
          .map((farm) => ({
            id: pick(farm?.farmId, farm?.FarmID, farm?.farmID, farm?.id, farm?.ID),
            name: pick(farm?.name, farm?.farmName, farm?.FarmName),
          }))
          .filter((farm) => farm.id != null && farm.name);

        if (didCancel) return;
        setFarmOptions(normalizedFarms);
        setFilters((currentFilters) => ({
          ...currentFilters,
          farm: storedFarmId || (normalizedFarms.length ? `${normalizedFarms[0].id}` : ""),
        }));
      } catch (error) {
        if (!didCancel) console.warn("Unable to hydrate farm options for analytics", error);
      }
    };

    loadFarms();
    return () => {
      didCancel = true;
    };
  }, [isStandaloneAnalytics]);

  useEffect(() => {
    if (isStandaloneAnalytics) return undefined;
    if (!filters.farm) {
      setCycleOptions([]);
      setFilters((currentFilters) => ({ ...currentFilters, cycle: "" }));
      return undefined;
    }

    let didCancel = false;
    setCycleOptions([]);
    setFilters((currentFilters) => ({ ...currentFilters, cycle: "" }));

    const loadCycles = async () => {
      try {
        const response = await axios.get(getCyclesUrl());
        const cyclesData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const pick = (...values) =>
          values.find((value) => value !== undefined && value !== null && `${value}`.length > 0);
        const normalizedCycles = cyclesData
          .map((cycle) => ({
            id: pick(cycle?.id, cycle?.ID, cycle?.cycleId, cycle?.cycleID, cycle?.CycleID),
            name: pick(cycle?.name, cycle?.cycleName, cycle?.CycleName, cycle?.cycle, cycle?.Cycle),
            farmId: pick(cycle?.farmId, cycle?.farmID, cycle?.FarmId, cycle?.FarmID),
          }))
          .filter(
            (cycle) => cycle.id != null && cycle.name && `${cycle.farmId}` === `${filters.farm}`
          );
        const uniqueCycles = Array.from(
          normalizedCycles
            .reduce((cyclesByName, cycle) => {
              const name = `${cycle.name}`;
              const existing = cyclesByName.get(name);
              if (existing) {
                if (!existing.cycleIds.some((id) => `${id}` === `${cycle.id}`)) {
                  existing.cycleIds.push(cycle.id);
                }
              } else {
                cyclesByName.set(name, { id: name, name, cycleIds: [cycle.id] });
              }
              return cyclesByName;
            }, new Map())
            .values()
        );

        if (didCancel) return;
        setCycleOptions(uniqueCycles);
        setFilters((currentFilters) => ({
          ...currentFilters,
          cycle: "",
        }));
      } catch (error) {
        if (!didCancel) console.warn("Unable to hydrate cycle options for analytics", error);
      }
    };

    loadCycles();
    return () => {
      didCancel = true;
    };
  }, [filters.farm, isStandaloneAnalytics]);

  const handleSummarySubmit = async () => {
    if (filters.type !== "Summary" || !filters.farm) return;

    setSummaryRows([]);
    setSummaryError("");
    setSummaryLoading(true);

    try {
      const selectedCycle = cycleOptions.find((cycle) => `${cycle.name}` === `${filters.cycle}`);
      const cycleName = selectedCycle?.name ?? null;
      const selectedDay = filters.day === "" ? null : filters.day;
      const response = await axios.post(
        getBroilerSummaryUrl(filters.farm, cycleName, selectedDay),
        null,
        SKIP_AUTH_CONFIG
      );
      const payload = response.data?.data ?? response.data;
      const rows = Array.isArray(payload) ? payload : payload ? [payload] : [];
      setSummaryRows(rows);
    } catch (error) {
      console.warn("Unable to load analytics summary", error);
      setSummaryRows([]);
      setSummaryError("Failed to load summary data.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleCatchSubmit = async () => {
    if (filters.type !== "Catch" || !filters.farm || !filters.cycle) return;

    const selectedCycle = cycleOptions.find((cycle) => `${cycle.name}` === `${filters.cycle}`);
    const cycleIds = selectedCycle?.cycleIds || [];
    if (!cycleIds.length) return;

    setCatchRows([]);
    setCatchError("");
    setCatchLoading(true);

    try {
      const [responses, facilitiesResponse] = await Promise.all([
        Promise.all(
          cycleIds.map((cycleId) => axios.get(getCatchSummaryUrl(filters.farm, cycleId)))
        ),
        axios.get(broilersByFarmUrl(filters.farm)),
      ]);
      const facilitiesPayload = facilitiesResponse.data?.data ?? facilitiesResponse.data;
      const facilities = Array.isArray(facilitiesPayload) ? facilitiesPayload : [];
      const facilityNamesById = new Map(
        facilities
          .filter((facility) => facility?.id != null)
          .map((facility) => [`${facility.id}`, facility.name])
      );
      const rows = responses.flatMap((response) => {
        const payload = response.data?.data ?? response.data;
        return Array.isArray(payload) ? payload : payload ? [payload] : [];
      });
      setCatchRows(
        rows.map((row) => {
          const facilityId = row?.facility_id ?? row?.facilityId ?? row?.facilityID;
          return {
            ...row,
            facilityName:
              facilityNamesById.get(`${facilityId}`) ??
              row?.facilityName ??
              row?.broilerName ??
              row?.name,
          };
        })
      );
    } catch (error) {
      console.warn("Unable to load catch summary", error);
      setCatchRows([]);
      setCatchError("Failed to load catch summary data.");
    } finally {
      setCatchLoading(false);
    }
  };

  const handleCostSubmit = async () => {
    if (filters.type !== "Costs" || !filters.farm || !filters.cycle) return;

    const selectedCycle = cycleOptions.find((cycle) => `${cycle.name}` === `${filters.cycle}`);
    const cycleIds = selectedCycle?.cycleIds || [];
    if (!cycleIds.length) return;

    setCostRows([]);
    setCostError("");
    setCostLoading(true);

    try {
      const [responses, facilitiesResponse] = await Promise.all([
        Promise.all(cycleIds.map((cycleId) => axios.get(getCostSummaryUrl(filters.farm, cycleId)))),
        axios.get(broilersByFarmUrl(filters.farm)),
      ]);
      const facilitiesPayload = facilitiesResponse.data?.data ?? facilitiesResponse.data;
      const facilities = Array.isArray(facilitiesPayload) ? facilitiesPayload : [];
      const facilityNamesById = new Map(
        facilities
          .filter((facility) => facility?.id != null)
          .map((facility) => [`${facility.id}`, facility.name])
      );
      const cycleNamesById = new Map(
        cycleOptions.flatMap((cycle) =>
          (cycle?.cycleIds || []).map((cycleId) => [`${cycleId}`, cycle.name])
        )
      );
      const rows = responses.flatMap((response) => {
        const payload = response.data?.data ?? response.data;
        return Array.isArray(payload) ? payload : payload ? [payload] : [];
      });
      setCostRows(
        rows.map((row) => ({
          ...row,
          farmId: filters.farm,
          facilityName:
            facilityNamesById.get(`${row?.facilityId}`) ?? row?.facilityName ?? row?.facilityId,
          cycleName: cycleNamesById.get(`${row?.cycleId}`) ?? row?.cycleName ?? row?.cycleId,
        }))
      );
    } catch (error) {
      console.warn("Unable to load cost summary", error);
      setCostRows([]);
      setCostError("Failed to load cost summary data.");
    } finally {
      setCostLoading(false);
    }
  };

  const handleSubmit = () => {
    if (filters.type === "Catch") {
      handleCatchSubmit();
    } else if (filters.type === "Costs") {
      handleCostSubmit();
    } else {
      handleSummarySubmit();
    }
  };

  const catchValue = (row, ...keys) => {
    const value = keys.map((key) => row?.[key]).find((candidate) => candidate != null);
    return value ?? "-";
  };

  const costValue = (row, key) => {
    const value = row?.[key];
    return value === null || value === undefined || value === "" ? "-" : value;
  };

  const handleSummarySort = (orderBy) => {
    setSummarySort((currentSort) => ({
      orderBy,
      order: currentSort.orderBy === orderBy && currentSort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedSummaryRows = useMemo(() => {
    const rows = [...summaryRows];
    const { orderBy, order } = summarySort;
    const textField = orderBy === "facilityName";

    return rows.sort((left, right) => {
      const leftValue = left?.[orderBy];
      const rightValue = right?.[orderBy];
      const leftEmpty = leftValue == null || leftValue === "";
      const rightEmpty = rightValue == null || rightValue === "";
      if (leftEmpty && rightEmpty) return 0;
      if (leftEmpty) return 1;
      if (rightEmpty) return -1;

      const comparison = textField
        ? String(leftValue).localeCompare(String(rightValue), undefined, {
            numeric: true,
            sensitivity: "base",
          })
        : Number(leftValue) - Number(rightValue);
      return order === "asc" ? comparison : -comparison;
    });
  }, [summaryRows, summarySort]);

  const sortedCatchRows = useMemo(() => {
    const getDateTime = (row) => {
      const value = row?.date ?? row?.Date ?? row?.batchDate ?? row?.datePlaced ?? row?.placeDate;
      return parseCatchDateTime(value)?.getTime() ?? null;
    };

    return catchRows
      .map((row, index) => ({
        row,
        index,
        timestamp: getDateTime(row),
        hasPlacementWeight: row?.placementWeight != null,
        facilityName: String(row?.facilityName ?? row?.broilerName ?? row?.name ?? ""),
      }))
      .sort((left, right) => {
        const comparePlacementWeight = () => {
          if (left.hasPlacementWeight !== right.hasPlacementWeight) {
            return left.hasPlacementWeight ? -1 : 1;
          }
          return left.index - right.index;
        };

        const nameComparison = left.facilityName.localeCompare(right.facilityName, undefined, {
          numeric: true,
          sensitivity: "base",
        });
        if (nameComparison !== 0) {
          return catchNameOrder === "asc" ? nameComparison : -nameComparison;
        }

        if (left.timestamp == null && right.timestamp == null) return comparePlacementWeight();
        if (left.timestamp == null) return 1;
        if (right.timestamp == null) return -1;
        const comparison = left.timestamp - right.timestamp;
        if (comparison !== 0) return comparison;
        return comparePlacementWeight();
      })
      .map(({ row }) => row);
  }, [catchNameOrder, catchRows]);

  const exportSummary = () => {
    exportExcelCsv(
      "reports-summary.csv",
      [
        "Date",
        "Day",
        "Facility",
        "Cycle",
        "Weight (kg)",
        "FCR",
        "Mortality",
        "Culls",
        "Mortality Rate",
        "Feed Bin A",
        "Feed Bin B",
        "Water Reading",
        "Water Source (%)",
        "Gas",
        "Electricity",
        "Solar",
      ],
      sortedSummaryRows.map((row) => [
        formatSummaryDate(row?.date),
        row?.cycleDay,
        row?.facilityName,
        row?.cycleName,
        row?.weightAvg,
        row?.fcr,
        row?.mortality,
        row?.culls,
        row?.mortalityRate,
        row?.bin1Reading,
        row?.bin2Reading,
        row?.waterReading,
        row?.utilityWater,
        row?.utilityGas,
        row?.utilityElectricity,
        row?.utilitySolar,
      ])
    );
  };

  const exportCatch = () => {
    exportExcelCsv(
      "reports-catch.csv",
      [
        "Date",
        "Facility Name",
        "Capacity",
        "Placement Weight",
        "Placement Avg",
        "Parent Age",
        "Placement Qty",
        "Breeder Code",
        "Mortalities",
        "Culls",
        "Catch Weight",
        "Catch Weight Diff.",
        "Abattoir Weight",
        "Farm Del. No.",
        "Abattoir Del. No.",
      ],
      sortedCatchRows.map((row) => [
        formatCatchDateTime(row?.date ?? row?.Date),
        catchValue(row, "facilityName", "broilerName", "name"),
        catchValue(row, "capacity", "facilityCapacity"),
        catchValue(row, "placementWeight", "placedWeight"),
        formatPlacementAverage(row),
        catchValue(row, "parentAge"),
        catchValue(
          row,
          "placementQty",
          "docQty",
          "placementQuantity",
          "placedQuantity",
          "quantity"
        ),
        catchValue(row, "breederCode"),
        catchValue(row, "mortalities", "mortality"),
        catchValue(row, "culls"),
        catchValue(row, "catchWeight", "caughtWeight"),
        getCatchWeightDifference(row),
        catchValue(row, "abattoirWeight", "slaughterWeight"),
        catchValue(row, "farmDeliveryNumber", "farmDeliveryNo"),
        catchValue(row, "abattoirDeliveryNumber", "abattoirDeliveryNo"),
      ])
    );
  };

  useEffect(() => {
    const tableContainer = summaryTableRef.current;
    if (!tableContainer || filters.type !== "Summary") return undefined;

    const updateFloatingScrollbar = () => {
      const rect = tableContainer.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = Math.max(0, rect.left);
      const width = Math.max(0, Math.min(rect.right, viewportWidth) - left);
      const visible =
        tableContainer.scrollWidth > tableContainer.clientWidth &&
        rect.top < viewportHeight &&
        rect.bottom > viewportHeight;

      setFloatingScrollbar({
        visible,
        left,
        width,
        contentWidth: tableContainer.scrollWidth,
      });

      if (floatingScrollbarRef.current) {
        floatingScrollbarRef.current.scrollLeft = tableContainer.scrollLeft;
      }
    };

    const handleViewportScroll = (event) => {
      if (event.target === tableContainer || event.target === floatingScrollbarRef.current) return;
      updateFloatingScrollbar();
    };

    const resizeObserver = new ResizeObserver(updateFloatingScrollbar);
    resizeObserver.observe(tableContainer);
    window.addEventListener("resize", updateFloatingScrollbar);
    window.addEventListener("scroll", handleViewportScroll, true);
    updateFloatingScrollbar();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateFloatingScrollbar);
      window.removeEventListener("scroll", handleViewportScroll, true);
    };
  }, [filters.type]);

  const handleSummaryTableScroll = (event) => {
    if (floatingScrollbarRef.current) {
      floatingScrollbarRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const handleFloatingScrollbarScroll = (event) => {
    if (summaryTableRef.current) {
      summaryTableRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isStandaloneAnalytics && (
          <Grid container spacing={3}>
            {highlightCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.id}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="button" color="text">
                      {card.title}
                    </MDTypography>
                    <MDTypography variant="h4" mt={1} mb={0.5}>
                      {card.value}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {card.helper}
                    </MDTypography>
                    <MDTypography variant="caption" color="success" display="block" mt={1}>
                      {card.trend}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!isStandaloneAnalytics && (
          <>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {analyticsFilters.map((filter) => (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <Typography>{filter.label}:</Typography>
                  <Select
                    fullWidth
                    value={filters[filter.id]}
                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                    disabled={filter.id === "day" && ["Catch", "Costs"].includes(filters.type)}
                    sx={{
                      height: "1cm",
                      "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                        height: "1cm !important",
                      },
                      "& .MuiSelect-select": {
                        minHeight: "1cm !important",
                        lineHeight: "1cm !important",
                        display: "flex",
                        alignItems: "center",
                        py: 0,
                      },
                    }}
                  >
                    {filter.id === "cycle" && <MenuItem value="">All Cycles</MenuItem>}
                    {filter.id === "day" && <MenuItem value="">All</MenuItem>}
                    {(filter.id === "farm"
                      ? farmOptions
                      : filter.id === "cycle"
                      ? cycleOptions
                      : filter.options
                    ).map((option) => (
                      <MenuItem
                        key={option?.id ?? option?.name ?? option}
                        value={option?.id ?? option}
                      >
                        {filter.id === "type" && option === "Summary"
                          ? "Production"
                          : option?.name ?? option}
                      </MenuItem>
                    ))}
                  </Select>
                  {filter.id === "type" && (
                    <MDBox sx={{ display: "flex", justifyContent: "flex-end", mt: "0.5cm" }}>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={handleSubmit}
                        disabled={
                          !filters.farm ||
                          summaryLoading ||
                          catchLoading ||
                          costLoading ||
                          (["Catch", "Costs"].includes(filters.type) && !filters.cycle)
                        }
                        sx={{ minHeight: "1cm" }}
                      >
                        Submit
                      </Button>
                    </MDBox>
                  )}
                </Grid>
              ))}
            </Grid>

            {filters.type === "Summary" && (
              <Card sx={{ mt: "1cm", overflow: "hidden" }}>
                <MDBox p={2}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDTypography variant="h6">Summary</MDTypography>
                    <Button
                      variant="contained"
                      color="info"
                      onClick={exportSummary}
                      disabled={!sortedSummaryRows.length || summaryLoading}
                      aria-label="Export Summary to Excel"
                      title="Export to Excel"
                      sx={{ minWidth: "2.5rem", px: 1 }}
                    >
                      <Icon>file_download</Icon>
                    </Button>
                  </MDBox>
                  <TableContainer
                    ref={summaryTableRef}
                    component={MDBox}
                    onScroll={handleSummaryTableScroll}
                    sx={{
                      mt: 2,
                      boxShadow: "none",
                      width: "fit-content",
                      maxWidth: "100%",
                      mx: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="Analytics summary"
                      sx={{
                        tableLayout: "fixed",
                        width: "37.8cm",
                        minWidth: "37.8cm",
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid",
                          borderColor: "grey.300",
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                          p: 0.5,
                          textAlign: "center",
                          color: "text.primary",
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: "2.6cm" }} />
                        <col style={{ width: "1.8cm" }} />
                        <col style={{ width: "2.8cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.4cm" }} />
                        <col style={{ width: "2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2cm" }} />
                        <col style={{ width: "2.4cm" }} />
                        {Array.from({ length: 7 }, (_, index) => (
                          <col key={index} style={{ width: "2.2cm" }} />
                        ))}
                        <col style={{ width: "2cm" }} />
                      </colgroup>
                      <TableBody>
                        <TableRow sx={{ bgcolor: "grey.100" }}>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Date
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={summarySort.orderBy === "cycleDay"}
                              direction={
                                summarySort.orderBy === "cycleDay" ? summarySort.order : "asc"
                              }
                              onClick={() => handleSummarySort("cycleDay")}
                              sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                            >
                              Day
                            </TableSortLabel>
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={summarySort.orderBy === "facilityName"}
                              direction={
                                summarySort.orderBy === "facilityName" ? summarySort.order : "asc"
                              }
                              onClick={() => handleSummarySort("facilityName")}
                              sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                            >
                              Facility
                            </TableSortLabel>
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Cycle
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Weight (kg)
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={summarySort.orderBy === "fcr"}
                              direction={summarySort.orderBy === "fcr" ? summarySort.order : "asc"}
                              onClick={() => handleSummarySort("fcr")}
                              sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                            >
                              FCR
                            </TableSortLabel>
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Mortality
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Culls
                          </TableCell>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={summarySort.orderBy === "mortalityRate"}
                              direction={
                                summarySort.orderBy === "mortalityRate" ? summarySort.order : "asc"
                              }
                              onClick={() => handleSummarySort("mortalityRate")}
                              sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                            >
                              Mortality Rate
                            </TableSortLabel>
                          </TableCell>
                          <TableCell colSpan={2} align="center" sx={{ fontWeight: 700 }}>
                            Feed Reading
                          </TableCell>
                          <TableCell colSpan={2} align="center" sx={{ fontWeight: 700 }}>
                            Water
                          </TableCell>
                          <TableCell colSpan={3} align="center" sx={{ fontWeight: 700 }}>
                            Utilities
                          </TableCell>
                          <TableCell rowSpan={2} />
                        </TableRow>
                        <TableRow sx={{ bgcolor: "grey.100" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Bin A</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Bin B</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Reading</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Source (%)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Gas</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Electricity</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Solar</TableCell>
                        </TableRow>
                        {summaryLoading ? (
                          <TableRow>
                            <TableCell colSpan={17} align="center" sx={{ py: 2 }}>
                              <CircularProgress color="info" size={24} />
                            </TableCell>
                          </TableRow>
                        ) : summaryError ? (
                          <TableRow>
                            <TableCell
                              colSpan={17}
                              align="center"
                              sx={{ py: 2, color: "error.main" }}
                            >
                              {summaryError}
                            </TableCell>
                          </TableRow>
                        ) : sortedSummaryRows.length ? (
                          sortedSummaryRows.map((row, index) => (
                            <TableRow
                              key={`${row?.date ?? "summary"}-${
                                row?.facilityId ?? "facility"
                              }-${index}`}
                              sx={{ height: "0.64cm" }}
                            >
                              <TableCell>{formatSummaryDate(row?.date)}</TableCell>
                              <TableCell>{row?.cycleDay ?? "-"}</TableCell>
                              <TableCell>{row?.facilityName ?? "-"}</TableCell>
                              <TableCell>{row?.cycleName ?? "-"}</TableCell>
                              <TableCell>{row?.weightAvg ?? "-"}</TableCell>
                              <TableCell>{row?.fcr ?? "-"}</TableCell>
                              <TableCell>{row?.mortality ?? "-"}</TableCell>
                              <TableCell>{row?.culls ?? "-"}</TableCell>
                              <TableCell>{row?.mortalityRate ?? "-"}</TableCell>
                              <TableCell>{row?.bin1Reading ?? "-"}</TableCell>
                              <TableCell>{row?.bin2Reading ?? "-"}</TableCell>
                              <TableCell>{row?.waterReading ?? "-"}</TableCell>
                              <TableCell>{row?.utilityWater ?? "-"}</TableCell>
                              <TableCell>{row?.utilityGas ?? "-"}</TableCell>
                              <TableCell>{row?.utilityElectricity ?? "-"}</TableCell>
                              <TableCell>{row?.utilitySolar ?? "-"}</TableCell>
                              <TableCell>
                                <Typography
                                  component={RouterLink}
                                  to={buildReportsUrl(row)}
                                  sx={{
                                    color: "#1976d2 !important",
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    fontWeight: "inherit",
                                    lineHeight: "inherit",
                                    textDecoration: "none",
                                    "&:visited": { color: "#1976d2 !important" },
                                    "&:hover": { color: "#1565c0 !important" },
                                  }}
                                >
                                  Report
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={17}
                              align="center"
                              sx={{ py: 2, color: "text.secondary" }}
                            >
                              No summary data available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
                {floatingScrollbar.visible && (
                  <MDBox
                    ref={floatingScrollbarRef}
                    onScroll={handleFloatingScrollbarScroll}
                    sx={{
                      position: "fixed",
                      left: floatingScrollbar.left,
                      bottom: 8,
                      width: floatingScrollbar.width,
                      height: 18,
                      overflowX: "auto",
                      overflowY: "hidden",
                      bgcolor: "background.paper",
                      boxShadow: 3,
                      zIndex: 1300,
                    }}
                  >
                    <MDBox sx={{ width: floatingScrollbar.contentWidth, height: 1 }} />
                  </MDBox>
                )}
              </Card>
            )}

            {filters.type === "Costs" && (
              <Card sx={{ mt: "1cm", overflow: "hidden" }}>
                <MDBox p={2}>
                  <MDTypography variant="h6">Costs</MDTypography>
                  <TableContainer
                    component={MDBox}
                    sx={{
                      mt: 2,
                      boxShadow: "none",
                      width: "fit-content",
                      maxWidth: "100%",
                      mx: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="Analytics costs"
                      sx={{
                        tableLayout: "fixed",
                        width: "20.4cm",
                        minWidth: "20.4cm",
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid",
                          borderColor: "grey.300",
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                          p: 0.5,
                          textAlign: "center",
                          color: "text.primary",
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "2.4cm" }} />
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "3cm" }} />
                      </colgroup>
                      <TableBody>
                        <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Cycle</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Unit Total</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Cost
                            <MDBox component="span" display="block">
                              (ex VAT)
                            </MDBox>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Cost
                            <MDBox component="span" display="block">
                              (inc VAT)
                            </MDBox>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Report</TableCell>
                        </TableRow>
                        {costLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                              <CircularProgress color="info" size={24} />
                            </TableCell>
                          </TableRow>
                        ) : costError ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{ py: 2, color: "error.main" }}
                            >
                              {costError}
                            </TableCell>
                          </TableRow>
                        ) : costRows.length ? (
                          costRows.map((row, index) => (
                            <TableRow
                              key={`${row?.facilityId ?? "facility"}-${row?.cycleId ?? "cycle"}-${
                                row?.category ?? "category"
                              }-${index}`}
                              sx={{ height: "0.64cm" }}
                            >
                              <TableCell>{costValue(row, "facilityName")}</TableCell>
                              <TableCell>{costValue(row, "cycleName")}</TableCell>
                              <TableCell>{costValue(row, "category")}</TableCell>
                              <TableCell>{formatCostSummaryNumber(row?.unitTotal)}</TableCell>
                              <TableCell>{formatCostSummaryNumber(row?.costExVat)}</TableCell>
                              <TableCell>{formatCostSummaryNumber(row?.costIncVat)}</TableCell>
                              <TableCell>
                                <Typography
                                  component={RouterLink}
                                  to={buildReportsUrl(row)}
                                  sx={{
                                    color: "#1976d2 !important",
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    fontWeight: "inherit",
                                    lineHeight: "inherit",
                                    textDecoration: "none",
                                    "&:visited": { color: "#1976d2 !important" },
                                    "&:hover": { color: "#1565c0 !important" },
                                  }}
                                >
                                  Report
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{ py: 2, color: "text.secondary" }}
                            >
                              No cost summary data available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Card>
            )}

            {filters.type === "Catch" && (
              <Card sx={{ mt: "1cm", overflow: "hidden" }}>
                <MDBox p={2}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDTypography variant="h6">Catch</MDTypography>
                    <Button
                      variant="contained"
                      color="info"
                      onClick={exportCatch}
                      disabled={!sortedCatchRows.length || catchLoading}
                      aria-label="Export Catch to Excel"
                      title="Export to Excel"
                      sx={{ minWidth: "2.5rem", px: 1 }}
                    >
                      <Icon>file_download</Icon>
                    </Button>
                  </MDBox>
                  <TableContainer
                    component={MDBox}
                    sx={{
                      mt: 2,
                      boxShadow: "none",
                      width: "fit-content",
                      maxWidth: "100%",
                      mx: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="Analytics catch"
                      sx={{
                        tableLayout: "fixed",
                        width: "33.6cm",
                        minWidth: "33.6cm",
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid",
                          borderColor: "grey.300",
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                          p: 0.5,
                          textAlign: "center",
                          color: "text.primary",
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: "3.2cm" }} />
                        <col style={{ width: "2.4cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "1.8cm" }} />
                        <col style={{ width: "2.6cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "1.8cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.2cm" }} />
                        <col style={{ width: "2.4cm" }} />
                        <col style={{ width: "2.6cm" }} />
                      </colgroup>
                      <TableBody>
                        <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                            Date
                          </TableCell>
                          <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                            Facility
                          </TableCell>
                          <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                            Placement
                          </TableCell>
                          <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                            Mortalities
                          </TableCell>
                          <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                            Catch
                          </TableCell>
                          <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                            Abattoir
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active
                              direction={catchNameOrder}
                              onClick={() =>
                                setCatchNameOrder((order) => (order === "asc" ? "desc" : "asc"))
                              }
                              sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Capacity</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Weight</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Avg</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Parent Age</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Breeder Code</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Mortalities</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Culls</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Weight</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Difference</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Weight</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Farm Del. No.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Abattoir Del. No.</TableCell>
                        </TableRow>
                        {catchLoading ? (
                          <TableRow>
                            <TableCell colSpan={15} align="center" sx={{ py: 2 }}>
                              <CircularProgress color="info" size={24} />
                            </TableCell>
                          </TableRow>
                        ) : catchError ? (
                          <TableRow>
                            <TableCell
                              colSpan={15}
                              align="center"
                              sx={{ py: 2, color: "error.main" }}
                            >
                              {catchError}
                            </TableCell>
                          </TableRow>
                        ) : sortedCatchRows.length ? (
                          sortedCatchRows.map((row, index) => (
                            <TableRow
                              key={`${catchValue(
                                row,
                                "facilityId",
                                "facilityID",
                                "broilerID"
                              )}-${index}`}
                            >
                              <TableCell>{formatCatchDateTime(row?.date ?? row?.Date)}</TableCell>
                              <TableCell>
                                {catchValue(row, "facilityName", "broilerName", "name")}
                              </TableCell>
                              <TableCell>
                                {catchValue(row, "capacity", "facilityCapacity")}
                              </TableCell>
                              <TableCell>
                                {catchValue(row, "placementWeight", "placedWeight")}
                              </TableCell>
                              <TableCell>{formatPlacementAverage(row)}</TableCell>
                              <TableCell>{catchValue(row, "parentAge")}</TableCell>
                              <TableCell>
                                {catchValue(
                                  row,
                                  "placementQty",
                                  "docQty",
                                  "placementQuantity",
                                  "placedQuantity",
                                  "quantity"
                                )}
                              </TableCell>
                              <TableCell>{catchValue(row, "breederCode")}</TableCell>
                              <TableCell>{catchValue(row, "mortalities", "mortality")}</TableCell>
                              <TableCell>{catchValue(row, "culls")}</TableCell>
                              <TableCell>
                                {catchValue(row, "catchWeight", "caughtWeight")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    Number(getCatchWeightDifference(row)) < 0
                                      ? "error.main !important"
                                      : "inherit",
                                }}
                              >
                                {getCatchWeightDifference(row)}
                              </TableCell>
                              <TableCell>
                                {catchValue(row, "abattoirWeight", "slaughterWeight")}
                              </TableCell>
                              <TableCell>
                                {catchValue(row, "farmDeliveryNumber", "farmDeliveryNo")}
                              </TableCell>
                              <TableCell>
                                {catchValue(row, "abattoirDeliveryNumber", "abattoirDeliveryNo")}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={15}
                              align="center"
                              sx={{ py: 2, color: "text.secondary" }}
                            >
                              No catch data available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Card>
            )}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
